import Component from '@glimmer/component';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatAsDollars } from 'tio-common/utils/format';
import { htmlSafe } from '@ember/template';
import { PhTrendUp, PhCalendarCheck, PhCurrencyDollar, PhSparkle } from 'ember-phosphor-icons';
import type { PslfProjection } from 'tio-employee/services/career-pathways-api';

interface ForgivenessFortuneTellerSignature {
  Args: {
    projection: PslfProjection;
  };
  Element: HTMLDivElement;
}

export default class ForgivenessFortuneTeller extends Component<ForgivenessFortuneTellerSignature> {
  get progressPercent() {
    return Math.round((this.args.projection.qualifyingPaymentsToDate / 120) * 100);
  }

  get progressStyle() {
    return htmlSafe(`width: ${this.progressPercent}%`);
  }

  get earnedAmount() {
    return this.args.projection.earnedToDate;
  }

  get remainingAmount() {
    return this.args.projection.projectedForgivenessAmount - this.earnedAmount;
  }

  get monthlyRate() {
    return this.args.projection.monthlyForgivenessRate;
  }

  get yearlyValue() {
    return this.monthlyRate * 12;
  }

  get forgivenessDate() {
    return new Date(this.args.projection.projectedForgivenessDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  get yearsRemaining() {
    return Math.round((this.args.projection.paymentsRemaining / 12) * 10) / 10;
  }

  // Pre-format dollar amounts
  get formattedProjectedAmount() {
    return formatAsDollars(this.args.projection.projectedForgivenessAmount, { cents: false });
  }

  get formattedEarnedAmount() {
    return formatAsDollars(this.earnedAmount, { cents: false });
  }

  get formattedRemainingAmount() {
    return formatAsDollars(this.remainingAmount, { cents: false });
  }

  get formattedMonthlyRate() {
    return formatAsDollars(this.monthlyRate, { cents: false });
  }

  get formattedYearlyValue() {
    return formatAsDollars(this.yearlyValue, { cents: false });
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="tio-hero tio-hero--cool rounded-2xl p-6 md:p-8 shadow-xl" ...attributes>
      {{! Header }}
      <div class="tio-row mb-6">
        <div class="p-2 bg-white/20 rounded-xl">
          <PhSparkle @size={{28}} class="text-yellow-300" @weight="fill" />
        </div>
        <div>
          <Heading @level={{2}} @size="xl" class="tio-fg-inverse md:text-2xl font-bold">
            Your Forgiveness Fortune
          </Heading>
          <span class="tio-on-hero-label text-sm">Tax-free wealth you're building</span>
        </div>
      </div>

      {{! Main Fortune Display }}
      <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6 border border-white/20">
        <div class="text-center">
          <span class="tio-on-hero-label tio-text-sm-medium uppercase tracking-wider">
            Projected Tax-Free Forgiveness
          </span>
          <div class="mt-2 mb-1">
            <span class="tio-text-4xl-bold md:text-5xl tracking-tight">
              {{this.formattedProjectedAmount}}
            </span>
          </div>
          <span class="tio-on-hero-caption text-sm">
            in
            {{this.yearsRemaining}}
            years
          </span>
        </div>
      </div>

      {{! Progress Bar }}
      <div class="mb-6">
        <div class="tio-flex-between text-sm mb-2">
          <span class="tio-on-hero-label">
            <span class="font-semibold tio-fg-inverse">{{this.formattedEarnedAmount}}</span>
            earned
          </span>
          <span class="tio-on-hero-label">
            <span class="font-semibold tio-fg-inverse">{{this.formattedRemainingAmount}}</span>
            to go
          </span>
        </div>
        <div class="h-4 bg-white/20 rounded-full overflow-hidden">
          <div
            class="tio-fortune-meter-fill h-full rounded-full transition-all duration-1000 ease-out relative"
            style={{this.progressStyle}}
          >
            <div
              class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"
            ></div>
          </div>
        </div>
        <div class="tio-flex-between mt-2">
          <span class="text-xs tio-on-hero-caption">
            {{@projection.qualifyingPaymentsToDate}}
            payments made
          </span>
          <Text @size="sm" @weight="semibold" class="tio-fg-inverse">
            {{this.progressPercent}}% complete
          </Text>
        </div>
      </div>

      {{! Stats Grid }}
      <div class="grid grid-cols-2 gap-3">
        <div class="bg-white/10 rounded-xl p-4 border border-white/10">
          <div class="tio-row-tight mb-1">
            <PhCalendarCheck @size={{18}} class="tio-on-hero-caption" />
            <span class="tio-on-hero-caption tio-text-xs-medium">Forgiveness Date</span>
          </div>
          <Text @weight="semibold" class="tio-fg-inverse">{{this.forgivenessDate}}</Text>
        </div>

        <div class="bg-white/10 rounded-xl p-4 border border-white/10">
          <div class="tio-row-tight mb-1">
            <PhCurrencyDollar @size={{18}} class="tio-on-hero-caption" />
            <span class="tio-on-hero-caption tio-text-xs-medium">Each Payment Earns</span>
          </div>
          <Text @weight="semibold" class="tio-fg-inverse">{{this.formattedMonthlyRate}}</Text>
        </div>
      </div>

      {{! Value Message }}
      <div class="tio-fortune-highlight mt-6 p-4 rounded-xl">
        <div class="tio-row-top">
          <PhTrendUp @size={{24}} class="text-yellow-300 shrink-0 mt-0.5" />
          <div>
            <Text @weight="medium" class="tio-fg-inverse">
              💡 Staying on track this year is worth
              <span class="font-bold text-yellow-300">
                {{this.formattedYearlyValue}}
              </span>
              to you.
            </Text>
            <span class="tio-on-hero-label text-sm mt-1 block">
              Every month you stay in public service adds
              {{this.formattedMonthlyRate}}
              to your forgiveness fortune.
            </span>
          </div>
        </div>
      </div>
    </div>
  </template>
}
